<template>
  <v-container fluid>
    <v-card>
      <v-card-title class="primary white--text mb-4">
        <v-icon left color="white">mdi-certificate</v-icon>
        สำรวจใบอนุญาตประกอบวิชาชีพเฉพาะ
        (ที่ไม่ใช่ใบอนุญาตประกอบวิชาชีพทางการศึกษา) ตำแหน่ง ครูผู้ช่วย
        <v-spacer></v-spacer>
        <v-btn color="white" outlined @click="openAddDialog">
          <v-icon left>mdi-plus</v-icon>
          เพิ่มใบอนุญาต
        </v-btn>
      </v-card-title>

      <!-- สรุปสถานะการส่งยืนยัน -->
      <v-card-text v-if="submissionSummary">
        <v-alert
          :type="submissionSummary.canSubmit ? 'info' : 'success'"
          dense
          text
          border="left"
        >
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <strong>สถานะการส่งยืนยัน:</strong>
              <span v-if="submissionSummary.canSubmit">
                มีรายการใบอนุญาต {{ submissionSummary.total }} รายการ ({{
                  submissionSummary.pending
                }}
                รายการรอส่ง)
              </span>
              <span v-else>
                ส่งยืนยันแล้วทั้งหมด {{ submissionSummary.submitted }} รายการ
                (รอสถานศึกษาตรวจสอบ
                {{ submissionSummary.pendingApproval }} รายการ)
              </span>
            </div>
            <v-btn
              v-if="submissionSummary.canSubmit"
              color="primary"
              @click="confirmSubmitAll"
              :disabled="submissionSummary.pending === 0"
            >
              <v-icon left>mdi-send</v-icon>
              ส่งยืนยันรายการทั้งหมด
            </v-btn>
          </div>
        </v-alert>
      </v-card-text>

      <!-- ตารางแสดงข้อมูล -->
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="licenses"
          :loading="loading"
          :search="searchTable"
          class="elevation-1"
          :items-per-page="15"
          :footer-props="{
            'items-per-page-options': [10, 15, 25, 50]
          }"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>รายการใบอนุญาต</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field
                v-model="searchTable"
                append-icon="mdi-magnify"
                label="ค้นหาในตาราง"
                single-line
                hide-details
                dense
                outlined
              ></v-text-field>
            </v-toolbar>
          </template>

          <template v-slot:item.full_name="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" class="mr-2" color="primary">
                <span class="white--text">{{
                  getInitials(item.full_name)
                }}</span>
              </v-avatar>
              <div>
                <div class="font-weight-bold">{{ item.full_name }}</div>
                <div class="caption grey--text">{{ item.id_card }}</div>
              </div>
            </div>
          </template>

          <template v-slot:item.license_type_name="{ item }">
            <div>
              <div class="font-weight-medium">{{ item.license_type_name }}</div>
              <div class="caption">{{ item.license_number }}</div>
            </div>
          </template>

          <template v-slot:item.expiry_date="{ item }">
            <div>
              <div>{{ formatDate(item.expiry_date) }}</div>
              <v-chip
                v-if="item.days_until_expiry !== null"
                x-small
                :color="getExpiryColor(item.days_until_expiry)"
                text-color="white"
              >
                {{ getExpiryText(item.days_until_expiry) }}
              </v-chip>
            </div>
          </template>

          <template v-slot:item.license_status="{ item }">
            <div>
              <v-chip
                small
                :color="getStatusColor(item.license_status)"
                text-color="white"
                class="mb-1"
              >
                {{ getStatusText(item.license_status) }}
              </v-chip>
              <br />
              <v-chip
                x-small
                :color="getApprovalStatusColor(item.approval_status)"
                text-color="white"
              >
                <v-icon x-small left>{{
                  getApprovalStatusIcon(item.approval_status)
                }}</v-icon>
                {{ getApprovalStatusText(item.approval_status) }}
              </v-chip>
            </div>
          </template>

          <template v-slot:item.license_file="{ item }">
            <v-btn
              v-if="item.license_file"
              icon
              small
              color="primary"
              @click="viewFile(item.license_file)"
            >
              <v-icon>mdi-file-document</v-icon>
            </v-btn>
            <span v-else class="grey--text">-</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="info"
                  class="mr-2"
                  @click="viewDetail(item)"
                  v-on="on"
                >
                  mdi-eye
                </v-icon>
              </template>
              <span>ดูรายละเอียด</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.approval_status === 'draft' || item.approval_status === 'rejected'">
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="primary"
                  class="mr-2"
                  @click="editItem(item)"
                  v-on="on"
                >
                  mdi-pencil
                </v-icon>
              </template>
              <span>แก้ไข</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.approval_status === 'draft' || item.approval_status === 'rejected'">
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="error"
                  class="mr-2"
                  @click="deleteItem(item)"
                  v-on="on"
                >
                  mdi-delete
                </v-icon>
              </template>
              <span>ลบ</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.approval_status === 'draft' || item.approval_status === 'rejected'">
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="success"
                  @click="submitSingleItem(item)"
                  v-on="on"
                >
                  mdi-send
                </v-icon>
              </template>
              <span>ส่งยืนยัน</span>
            </v-tooltip>

            <v-tooltip bottom v-if="item.approval_status === 'pending'">
              <template v-slot:activator="{ on }">
                <v-icon small color="grey" v-on="on">
                  mdi-clock-outline
                </v-icon>
              </template>
              <span>รอสถานศึกษาตรวจสอบ</span>
            </v-tooltip>
          </template>
        </v-data-table>
        <div class="pa-2 ">
          <v-icon color="error">mdi-alert</v-icon> กรณีไม่อนุมัติสามารถดำเนินการลบ และเพิ่มใหม่ได้ หรือแก้ไข และให้ดำเนินการกดส่ง <v-icon>mdi-send</v-icon>
        </div>
      </v-card-text>
    </v-card>

    <!-- Dialog เพิ่ม/แก้ไขใบอนุญาต -->
    <!-- Dialog เพิ่ม/แก้ไขใบอนุญาต -->
    <v-dialog v-model="dialogAdd" max-width="800px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-certificate</v-icon>
          {{ editMode ? "แก้ไขข้อมูลใบอนุญาต" : "เพิ่มข้อมูลใบอนุญาต" }}
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="form" v-model="valid">
            <v-row>
              <!-- แสดงข้อมูลครูผู้ช่วยที่ Login -->
              <v-col cols="12">
                <v-card outlined color="blue lighten-5">
                  <v-card-text class="py-2">
                    <div class="d-flex align-center">
                      <v-avatar color="primary" size="40" class="mr-3">
                        <span class="white--text">{{
                          getInitials(currentUserFullName)
                        }}</span>
                      </v-avatar>
                      <div>
                        <div class="font-weight-bold">
                          {{ currentUserFullName }}
                        </div>
                        <div class="caption grey--text">
                          {{ formData.id_card }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- ข้อมูลใบอนุญาต -->
              <v-col cols="12">
                <v-subheader class="px-0">
                  <v-icon class="mr-2">mdi-certificate</v-icon>
                  ข้อมูลใบอนุญาต
                </v-subheader>
              </v-col>

              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="formData.license_type_id"
                  :items="licenseTypes"
                  item-text="license_type_name"
                  item-value="license_type_id"
                  label="ประเภทใบอนุญาต *"
                  outlined
                  dense
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-certificate"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.license_number"
                  label="เลขที่ใบอนุญาต *"
                  outlined
                  dense
                  :rules="[rules.required]"
                  prepend-inner-icon="mdi-numeric"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.issuer_organization"
                  label="หน่วยงานที่ออกใบอนุญาต"
                  outlined
                  dense
                  prepend-inner-icon="mdi-office-building"
                ></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-menu
                  v-model="menuIssueDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="formData.issue_date"
                      label="วันที่ออกใบอนุญาต"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.issue_date"
                    @input="menuIssueDate = false"
                    locale="th"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="6">
                <v-menu
                  v-model="menuExpiryDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="formData.expiry_date"
                      label="วันที่หมดอายุ"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="formData.expiry_date"
                    @input="menuExpiryDate = false"
                    locale="th"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.license_status"
                  :items="statusOptions"
                  label="สถานะใบอนุญาต"
                  outlined
                  dense
                  prepend-inner-icon="mdi-information"
                ></v-select>
              </v-col>

              <!-- ส่วนหัวข้อวุฒิการศึกษา -->
              <v-col cols="12">
                <v-divider class="my-2"></v-divider>
                <v-subheader class="px-0">
                  <v-icon class="mr-2">mdi-school</v-icon>
                  ข้อมูลวุฒิการศึกษา
                </v-subheader>
              </v-col>

              <!-- ระดับการศึกษา -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.education_level"
                  :items="educationLevelOptions"
                  label="ระดับการศึกษา"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-school"
                  hint="ระดับการศึกษาสูงสุดที่จบ"
                  persistent-hint
                ></v-select>
              </v-col>

              <!-- สถานศึกษาที่จบ -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.institution_name"
                  label="สถานศึกษาที่จบ"
                  outlined
                  dense
                  prepend-inner-icon="mdi-domain"
                  placeholder="เช่น มหาวิทยาลัยเชียงใหม่"
                  hint="ระบุชื่อสถาบันการศึกษาที่จบการศึกษา"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- ปีการศึกษาที่จบ -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.graduation_year"
                  :items="yearOptions"
                  label="ปีการศึกษาที่จบ (พ.ศ.)"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-calendar-check"
                  hint="ปีที่สำเร็จการศึกษา"
                  persistent-hint
                ></v-select>
              </v-col>

              <!-- สาขาวิชา -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.major"
                  label="สาขาวิชา"
                  outlined
                  dense
                  prepend-inner-icon="mdi-book-open-variant"
                  placeholder="เช่น ครุศาสตร์เครื่องกล, วิทยาการคอมพิวเตอร์"
                  hint="ระบุสาขาวิชาที่จบการศึกษา"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- ส่วนไฟล์และหมายเหตุ -->
              <v-col cols="12">
                <v-divider class="my-2"></v-divider>
              </v-col>

              <v-col cols="12" md="12">
                <v-file-input
                  v-model="fileUpload"
                  label="อัพโหลดไฟล์ใบอนุญาต (PDF, JPG, PNG)"
                  outlined
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  accept=".pdf,.jpg,.jpeg,.png"
                  :hint="
                    formData.license_file
                      ? 'มีไฟล์แนบอยู่แล้ว'
                      : 'ขนาดไฟล์ไม่เกิน 5MB'
                  "
                  persistent-hint
                ></v-file-input>
              </v-col>

              <v-col cols="12" md="6" v-if="formData.license_file">
                <v-text-field
                  v-model="formData.license_file"
                  label="ไฟล์ที่แนบ"
                  outlined
                  dense
                  readonly
                  prepend-inner-icon="mdi-file"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="formData.remarks"
                  label="หมายเหตุ"
                  outlined
                  dense
                  rows="3"
                  prepend-inner-icon="mdi-note-text"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialog">ยกเลิก</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveLicense">
            บันทึก
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog ยืนยันการส่งรายการ -->
    <v-dialog v-model="dialogSubmitConfirm" max-width="500px" persistent>
      <v-card>
        <v-card-title class="warning white--text">
          <v-icon left color="white">mdi-alert</v-icon>
          ยืนยันการส่งรายการ
        </v-card-title>

        <v-card-text class="pt-4">
          <v-alert type="info" dense text>
            <div>
              <strong>คุณกำลังจะส่งยืนยันรายการใบอนุญาต</strong>
            </div>
            <div class="mt-2">
              จำนวน: <strong>{{ itemsToSubmit.length }}</strong> รายการ
            </div>
          </v-alert>

          <v-list
            dense
            class="mt-3"
            style="max-height: 300px; overflow-y: auto;"
          >
            <v-list-item v-for="item in itemsToSubmit" :key="item.license_id">
              <v-list-item-icon>
                <v-icon color="primary">mdi-certificate</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{
                  item.license_type_name
                }}</v-list-item-title>
                <v-list-item-subtitle>{{
                  item.license_number
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-alert type="warning" dense text class="mt-3">
            <div class="text-caption">
              <v-icon small class="mr-1">mdi-information</v-icon>
              หมายเหตุ: เมื่อส่งยืนยันแล้ว คุณจะไม่สามารถแก้ไขหรือลบรายการได้
              จนกว่าสถานศึกษาจะตรวจสอบเรียบร้อย
            </div>
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogSubmitConfirm = false">
            ยกเลิก
          </v-btn>
          <v-btn color="primary" :loading="submitting" @click="submitItems">
            <v-icon left>mdi-send</v-icon>
            ยืนยันการส่ง
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog แสดงรายละเอียด -->
    <v-dialog v-model="dialogDetail" max-width="700px">
      <v-card v-if="selectedItem">
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-information</v-icon>
          รายละเอียดใบอนุญาต
        </v-card-title>

        <v-card-text class="pt-4">
          <v-row dense>
            <v-col cols="12">
              <v-list dense>
                <v-list-item>
                  <v-list-item-icon
                    ><v-icon>mdi-account</v-icon></v-list-item-icon
                  >
                  <v-list-item-content>
                    <v-list-item-title>ชื่อ-นามสกุล</v-list-item-title>
                    <v-list-item-subtitle class="text-h6">{{
                      selectedItem.full_name
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon
                    ><v-icon>mdi-certificate</v-icon></v-list-item-icon
                  >
                  <v-list-item-content>
                    <v-list-item-title>ประเภทใบอนุญาต</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedItem.license_type_name
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon
                    ><v-icon>mdi-numeric</v-icon></v-list-item-icon
                  >
                  <v-list-item-content>
                    <v-list-item-title>เลขที่ใบอนุญาต</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedItem.license_number
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-icon
                    ><v-icon>mdi-information</v-icon></v-list-item-icon
                  >
                  <v-list-item-content>
                    <v-list-item-title>สถานะ</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-chip
                        small
                        :color="getStatusColor(selectedItem.license_status)"
                        text-color="white"
                        class="mr-2"
                      >
                        {{ getStatusText(selectedItem.license_status) }}
                      </v-chip>
                      <v-chip
                        x-small
                        :color="
                          getApprovalStatusColor(selectedItem.approval_status)
                        "
                        text-color="white"
                      >
                        {{
                          getApprovalStatusText(selectedItem.approval_status)
                        }}
                      </v-chip>
                      <div class="red--text mt-2 font-weight-bold">
                        {{ selectedItem.approval_remark }}
                      </div>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="my-3"></v-divider>

                <!-- ส่วนแสดงข้อมูลวุฒิการศึกษา -->
                <v-subheader class="px-0">
                  <v-icon class="mr-2">mdi-school</v-icon>
                  ข้อมูลวุฒิการศึกษา
                </v-subheader>

                <v-list-item v-if="selectedItem.education_level">
                  <v-list-item-icon>
                    <v-icon>mdi-school</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>ระดับการศึกษา</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedItem.education_level
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedItem.institution_name">
                  <v-list-item-icon>
                    <v-icon>mdi-domain</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>สถานศึกษาที่จบ</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedItem.institution_name
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedItem.graduation_year">
                  <v-list-item-icon>
                    <v-icon>mdi-calendar-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>ปีการศึกษาที่จบ</v-list-item-title>
                    <v-list-item-subtitle
                      >พ.ศ.
                      {{
                        parseInt(selectedItem.graduation_year) + 543
                      }}</v-list-item-subtitle
                    >
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="selectedItem.major">
                  <v-list-item-icon>
                    <v-icon>mdi-book-open-variant</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>สาขาวิชา</v-list-item-title>
                    <v-list-item-subtitle>{{
                      selectedItem.major
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <!-- แสดงข้อความถ้าไม่มีข้อมูลการศึกษา -->
                <v-list-item
                  v-if="
                    !selectedItem.education_level &&
                      !selectedItem.institution_name &&
                      !selectedItem.graduation_year &&
                      !selectedItem.major
                  "
                >
                  <v-list-item-content>
                    <v-list-item-subtitle class="grey--text text-center">
                      <v-icon small class="mr-1">mdi-information</v-icon>
                      ไม่มีข้อมูลวุฒิการศึกษา
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogDetail = false">ปิด</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="pdflicense_filedialog" max-width="80%">
      <v-card class="" elevation="2">
        <embed
          :src="'/HRvecLicenseTeach/' + license_file"
          width="100%"
          height="700px"
        />
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: "ServeyAssistantSpecializedLicenses",
  data() {
    return {
      ApiKey: "HRvec2021",
      pdflicense_filedialog: false,
      license_file: "",
      userSession: null,
      loading: false,
      saving: false,
      submitting: false,
      valid: false,
      dialogAdd: false,
      dialogDetail: false,
      dialogSubmitConfirm: false,
      editMode: false,
      searchTable: "",

      licenses: [],
      licenseTypes: [],
      currentUser: null,
      submissionSummary: null,
      itemsToSubmit: [],

      educationLevelOptions: [
        { text: "มัธยมศึกษาตอนปลาย (ม.6)", value: "มัธยมศึกษาตอนปลาย" },
        { text: "ประกาศนียบัตรวิชาชีพ (ปวช.)", value: "ปวช." },
        { text: "ประกาศนียบัตรวิชาชีพชั้นสูง (ปวส.)", value: "ปวส." },
        { text: "อนุปริญญา", value: "อนุปริญญา" },
        { text: "ปริญญาตรี", value: "ปริญญาตรี" },
        { text: "ประกาศนียบัตรบัณฑิต", value: "ประกาศนียบัตรบัณฑิต" },
        { text: "ปริญญาโท", value: "ปริญญาโท" },
        {
          text: "ประกาศนียบัตรบัณฑิตชั้นสูง",
          value: "ประกาศนียบัตรบัณฑิตชั้นสูง"
        },
        { text: "ปริญญาเอก", value: "ปริญญาเอก" }
      ],

      formData: {
        license_id: null,
        id_card: "",
        license_type_id: null,
        license_number: "",
        issue_date: null,
        expiry_date: null,
        issuer_organization: "",
        license_file: "",
        license_status: "active",
        approval_status: "draft",
        remarks: "",
        survey_date: new Date().toISOString().substr(0, 10),
        surveyor_name: "",
        education_level: "",
        institution_name: "",
        graduation_year: null,
        major: ""
      },

      fileUpload: null,
      selectedItem: null,

      menuIssueDate: false,
      menuExpiryDate: false,

      statusOptions: [
        { text: "ใช้งานได้", value: "active" },
        { text: "หมดอายุ", value: "expired" },
        { text: "ถูกเพิกถอน", value: "revoked" },
        { text: "รอดำเนินการ", value: "pending" }
      ],

      headers: [
        { text: "ชื่อ-นามสกุล", value: "full_name", width: 200 },
        { text: "สถานศึกษา", value: "college_name", width: 200 },
        { text: "ประเภทใบอนุญาต", value: "license_type_name", width: 250 },
        {
          text: "วันที่หมดอายุ",
          value: "expiry_date",
          align: "center",
          width: 150
        },
        { text: "สถานะ", value: "license_status", align: "center", width: 150 },
        {
          text: "ไฟล์",
          value: "license_file",
          align: "center",
          sortable: false,
          width: 80
        },
        {
          text: "จัดการ",
          value: "actions",
          align: "center",
          sortable: false,
          width: 180
        }
      ],

      snackbar: { show: false, text: "", color: "success" },
      rules: { required: v => !!v || "กรุณากรอกข้อมูล" }
    };
  },

  async mounted() {
    await this.initializeUser();
  },

  computed: {
    currentUserFullName() {
      return this.currentUser
        ? `${this.currentUser.title_s}${this.currentUser.frist_name} ${this.currentUser.last_name}`
        : "";
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear; i >= currentYear - 40; i--) {
        years.push({
          text: (i + 543).toString(), // แสดงเป็น พ.ศ.
          value: i // เก็บเป็น ค.ศ. ในฐานข้อมูล
        });
      }
      return years;
    }
  },

  methods: {
    async initializeUser() {
      try {
        this.loading = true;
        this.userSession = JSON.parse(localStorage.getItem("user")) || {};
        await this.loadCurrentUser();
        await this.initialize();
      } catch (error) {
        console.error("Error initializing user:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadCurrentUser() {
      try {
        const response = await this.$http.post("personnel_temporary.php", {
          ApiKey: this.ApiKey,
          id_card: this.userSession.id_card
        });

        if (response.data) {
          this.currentUser = response.data;
          if (this.currentUser.position_name != "ครูผู้ช่วย") {
            this.$router.push("/UserProfile");
          }
          this.formData.id_card = this.currentUser.id_card;
          this.formData.surveyor_name = this.currentUserFullName;
        }
      } catch (error) {
        console.error("Error loading current user:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้", "error");
      }
    },

    async initialize() {
      await this.loadLicenseTypes();
      await this.loadLicenses();
    },

    async loadLicenseTypes() {
      try {
        const response = await this.$http.post(
          "specialized_license_types.php",
          {
            ApiKey: "HRvec2021",
            is_active: 1
          }
        );
        this.licenseTypes = response.data || [];
      } catch (error) {
        console.error("Error loading license types:", error);
      }
    },

    async loadLicenses() {
      this.loading = true;
      try {
        const params = {
          ApiKey: "HRvec2021",
          id_card: this.currentUser.id_card
        };

        const response = await this.$http.post(
          "assistant_specialized_licenses_id.php",
          params
        );
        this.licenses = response.data || [];
        this.calculateSubmissionSummary();
      } catch (error) {
        console.error("Error loading licenses:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error");
      } finally {
        this.loading = false;
      }
    },

    calculateSubmissionSummary() {
      const total = this.licenses.length;
      const draft = this.licenses.filter(l => l.approval_status === "draft")
        .length;
      const pending = this.licenses.filter(l => l.approval_status === "pending")
        .length;
      const approved = this.licenses.filter(
        l => l.approval_status === "approved"
      ).length;

      this.submissionSummary = {
        total,
        pending: draft,
        submitted: pending + approved,
        pendingApproval: pending,
        approved,
        canSubmit: draft > 0
      };
    },

    confirmSubmitAll() {
      this.itemsToSubmit = this.licenses.filter(
        l => l.approval_status === "draft"
      );
      if (this.itemsToSubmit.length === 0) {
        this.showSnackbar("ไม่มีรายการที่ต้องส่งยืนยัน", "warning");
        return;
      }
      this.dialogSubmitConfirm = true;
    },

    submitSingleItem(item) {
      this.itemsToSubmit = [item];
      this.dialogSubmitConfirm = true;
    },

    async submitItems() {
      this.submitting = true;
      try {
        const licenseIds = this.itemsToSubmit.map(item => item.license_id);

        const response = await this.$http.post(
          "assistant_specialized_licenses.submit.php",
          {
            ApiKey: "HRvec2021",
            license_ids: licenseIds,
            id_card: this.currentUser.id_card,
            submitted_at: new Date()
              .toISOString()
              .substr(0, 19)
              .replace("T", " ")
          }
        );

        
        if (response.data.status) {
          this.showSnackbar(
            `ส่งยืนยันสำเร็จ ${licenseIds.length} รายการ`,
            "success"
          );
          this.dialogSubmitConfirm = false;
          this.itemsToSubmit = [];
          await this.loadLicenses();
        } else {
          this.showSnackbar(response.data.message || "เกิดข้อผิดพลาด", "error");
        }
      } catch (error) {
        console.error("Error submitting items:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งยืนยัน", "error");
      } finally {
        this.submitting = false;
      }
    },

    async saveLicense() {
      if (!this.$refs.form.validate()) {
        this.showSnackbar("กรุณากรอกข้อมูลให้ครบถ้วน", "warning");
        return;
      }

      if (!this.formData.id_card) {
        this.showSnackbar("ไม่พบข้อมูลผู้ใช้", "error");
        return;
      }

      // จัดการอัพโหลดไฟล์ก่อน (ถ้ามี)
      if (this.fileUpload != null) {
        try {
          // สร้างชื่อไฟล์
          let result_handle = await this.handleFileUpload(this.fileUpload);
          const filename = this.formData.license_file;

          // สร้าง FormData สำหรับอัพโหลด
          const formData = new FormData();
          formData.append("ApiKey", this.ApiKey);
          formData.append("file", this.fileUpload);
          formData.append("filename", `../HRvecLicenseTeach/${filename}`);

          // อัพโหลดไฟล์
          let result_upload = await this.$http.post(
            "uploadfile.php",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data"
              }
            }
          );

          // บันทึกชื่อไฟล์ที่อัพโหลดสำเร็จ
          this.formData.license_file = filename;
        } catch (error) {
          console.error("Error uploading file:", error);
          this.showSnackbar("เกิดข้อผิดพลาดในการอัพโหลดไฟล์", "error");
          return; // หยุดการบันทึกถ้าอัพโหลดล้มเหลว
        }
      }

      // บันทึกข้อมูลใบอนุญาต
      this.saving = true;
      try {
        const endpoint = this.editMode
          ? "assistant_specialized_licenses.update.php"
          : "assistant_specialized_licenses.insert.php";

        const dataToSend = {
          ApiKey: "HRvec2021",
          ...this.formData,
          updated_by: this.currentUser.id_card
        };

        const response = await this.$http.post(endpoint, dataToSend);
        if (response.data.status) {
          this.showSnackbar(
            this.editMode ? "แก้ไขข้อมูลสำเร็จ" : "เพิ่มข้อมูลสำเร็จ",
            "success"
          );
          this.closeDialog();
          await this.loadLicenses();
        } else {
          this.showSnackbar(response.data.message || "เกิดข้อผิดพลาด", "error");
        }
      } catch (error) {
        console.error("Error saving license:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึกข้อมูล", "error");
      } finally {
        this.saving = false;
      }
    },

    openAddDialog() {
      this.editMode = false;
      this.formData = {
        license_id: null,
        id_card: this.currentUser.id_card,
        license_type_id: null,
        license_number: "",
        issue_date: null,
        expiry_date: null,
        issuer_organization: "",
        license_file: "",
        license_status: "active",
        approval_status: "draft",
        remarks: "",
        survey_date: new Date().toISOString().substr(0, 10),
        surveyor_name: this.currentUserFullName,

        // เพิ่มฟิลด์วุฒิการศึกษา
        education_level: "",
        institution_name: "",
        graduation_year: null,
        major: ""
      };
      this.dialogAdd = true;
    },

    editItem(item) {
      if (item.id_card !== this.currentUser.id_card) {
        this.showSnackbar("คุณไม่สามารถแก้ไขข้อมูลของผู้อื่นได้", "error");
        return;
      }

      if (item.approval_status !== "draft" && item.approval_status !== 'rejected') {
        this.showSnackbar("ไม่สามารถแก้ไขรายการที่ส่งยืนยันแล้ว", "warning");
        return;
      }

      this.editMode = true;
      this.formData = { ...item };
      this.formData.survey_date = new Date().toISOString().substr(0, 10);
      this.dialogAdd = true;
    },

    async deleteItem(item) {
      if (item.id_card !== this.currentUser.id_card) {
        this.showSnackbar("คุณไม่สามารถลบข้อมูลของผู้อื่นได้", "error");
        return;
      }

      if (item.approval_status !== "draft" && item.approval_status !== 'rejected') {
        this.showSnackbar("ไม่สามารถลบรายการที่ส่งยืนยันแล้ว", "warning");
        return;
      }

      if (!confirm(`ต้องการลบใบอนุญาต ${item.license_number} หรือไม่?`)) {
        return;
      }

      try {
        const response = await this.$http.post(
          "assistant_specialized_licenses.delete.php",
          {
            ApiKey: "HRvec2021",
            license_id: item.license_id,
            id_card: this.currentUser.id_card
          }
        );

        if (response.data.status) {
          this.showSnackbar("ลบข้อมูลสำเร็จ", "success");
          await this.loadLicenses();
        } else {
          this.showSnackbar(response.data.message || "เกิดข้อผิดพลาด", "error");
        }
      } catch (error) {
        console.error("Error deleting license:", error);
        this.showSnackbar("เกิดข้อผิดพลาดในการลบข้อมูล", "error");
      }
    },

    viewDetail(item) {
      this.selectedItem = item;
      this.dialogDetail = true;
    },

    closeDialog() {
      this.dialogAdd = false;
      this.editMode = false;
      this.$refs.form.reset();
      this.formData = {
        license_id: null,
        id_card: this.currentUser.id_card,
        license_type_id: null,
        license_number: "",
        issue_date: null,
        expiry_date: null,
        issuer_organization: "",
        license_file: "",
        license_status: "active",
        approval_status: "draft",
        remarks: "",
        survey_date: new Date().toISOString().substr(0, 10),
        surveyor_name: this.currentUserFullName,

        // เพิ่มฟิลด์วุฒิการศึกษา
        education_level: "",
        institution_name: "",
        graduation_year: null,
        major: ""
      };
      this.fileUpload = null;
    },

    generateYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear; i >= currentYear - 50; i--) {
        years.push({
          text: (i + 543).toString(), // แสดงเป็น พ.ศ.
          value: i // เก็บเป็น ค.ศ. ในฐานข้อมูล
        });
      }
      return years;
    },

    async handleFileUpload(file) {
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          this.showSnackbar("ขนาดไฟล์ใหญ่เกินไป (ไม่เกิน 5MB)", "warning");
          this.fileUpload = null;
          return;
        }

        const fileExtension = file.name
          .split(".")
          .pop()
          .toLowerCase();
        const uniqueCode = this.generateUniqueCode(8);
        const fileName = `license_${
          this.currentUser.id_card
        }_${Date.now()}_${uniqueCode}.${fileExtension}`;
        this.formData.license_file = fileName;
      }
    },

    generateUniqueCode(length = 8) {
      const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = "";
      for (let i = 0; i < length; i++) {
        result += characters.charAt(
          Math.floor(Math.random() * characters.length)
        );
      }
      return result;
    },

    viewFile(fileName) {
      if (fileName) {
        this.license_file = "";
        this.license_file = fileName;
        this.pdflicense_filedialog = true;
      }
    },

    getInitials(fullName) {
      if (!fullName) return "";
      const parts = fullName.trim().split(" ");
      if (parts.length >= 2) {
        return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
      }
      return fullName.charAt(0);
    },

    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      const day = d
        .getDate()
        .toString()
        .padStart(2, "0");
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const year = d.getFullYear() + 543;
      return `${day}/${month}/${year}`;
    },

    getStatusColor(status) {
      const colors = {
        active: "success",
        expired: "error",
        revoked: "grey",
        pending: "warning"
      };
      return colors[status] || "grey";
    },

    getStatusText(status) {
      const texts = {
        active: "ใช้งานได้",
        expired: "หมดอายุ",
        revoked: "ถูกเพิกถอน",
        pending: "รอดำเนินการ"
      };
      return texts[status] || status;
    },

    getExpiryColor(days) {
      if (days < 0) return "error";
      if (days <= 30) return "error";
      if (days <= 90) return "warning";
      return "success";
    },

    getExpiryText(days) {
      if (days < 0) return "หมดอายุแล้ว";
      if (days === 0) return "หมดอายุวันนี้";
      if (days <= 30) return `เหลือ ${days} วัน`;
      if (days <= 90) return `เหลือ ${days} วัน`;
      return `เหลือ ${days} วัน`;
    },

    getApprovalStatusColor(status) {
      const colors = {
        draft: "grey",
        pending: "warning",
        approved: "success",
        rejected: "error"
      };
      return colors[status] || "grey";
    },

    getApprovalStatusText(status) {
      const texts = {
        draft: "ร่าง",
        pending: "รอตรวจสอบ",
        approved: "อนุมัติ",
        rejected: "ไม่อนุมัติ"
      };
      return texts[status] || status;
    },

    getApprovalStatusIcon(status) {
      const icons = {
        draft: "mdi-pencil",
        pending: "mdi-clock-outline",
        approved: "mdi-check-circle",
        rejected: "mdi-close-circle"
      };
      return icons[status] || "mdi-help-circle";
    },

    showSnackbar(text, color = "success") {
      this.snackbar = { show: true, text, color };
    }
  }
};
</script>

<style scoped>
.v-data-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}
</style>
